import { useState, useEffect } from 'react';
import { Plus, Trash2, X, GripVertical, Sparkles } from 'lucide-react';
import './Admin.css';

function authFetch(url, token, options = {}) {
  return fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers },
  });
}

const MODE_LABELS = { sequential: 'Sequential', free: 'Free' };

export default function LearningPaths({ token }) {
  const [paths, setPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [pathDetail, setPathDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', description: '', icon: '📚', color: '#f59e0b', mode: 'sequential' });
  const [newLoading, setNewLoading] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [decks, setDecks] = useState([]);
  const [addingDeck, setAddingDeck] = useState(false);
  const [selectedDeckId, setSelectedDeckId] = useState('');
  const [generatingDeckId, setGeneratingDeckId] = useState(null);
  const [genForm, setGenForm] = useState({ cardCount: 60, mode: 'concept', target: '' });
  const [genStatus, setGenStatus] = useState({});

  useEffect(() => {
    loadPaths();
    loadDecks();
  }, [token]);

  async function loadPaths() {
    try {
      const res = await authFetch('/api/admin/paths', token);
      if (res.ok) {
        const data = await res.json();
        setPaths(data.paths || data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadDecks() {
    try {
      const res = await authFetch('/api/admin/decks', token);
      if (res.ok) {
        const data = await res.json();
        setDecks(data.decks || data);
      }
    } catch {}
  }

  async function selectPath(path) {
    setSelectedPath(path);
    setEditForm(null);
    setDetailLoading(true);
    try {
      const res = await authFetch(`/api/admin/paths/${path.id}`, token);
      if (res.ok) {
        const data = await res.json();
        setPathDetail(data);
        setEditForm({
          name: data.name,
          description: data.description || '',
          icon: data.icon || '📚',
          color: data.color || '#f59e0b',
          mode: data.mode || 'sequential',
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setNewLoading(true);
    try {
      const res = await authFetch('/api/admin/paths', token, {
        method: 'POST',
        body: JSON.stringify(newForm),
      });
      if (!res.ok) throw new Error('Failed to create path');
      const created = await res.json();
      setPaths(p => [...p, created.path || created]);
      setShowNewForm(false);
      setNewForm({ name: '', description: '', icon: '📚', color: '#f59e0b', mode: 'sequential' });
    } catch (err) {
      setError(err.message);
    } finally {
      setNewLoading(false);
    }
  }

  async function handleUpdate(e) {
    e.preventDefault();
    if (!selectedPath) return;
    setSaving(true);
    try {
      const res = await authFetch(`/api/admin/paths/${selectedPath.id}`, token, {
        method: 'PUT',
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error('Failed to update path');
      const updated = await res.json();
      setPaths(p => p.map(x => x.id === selectedPath.id ? (updated.path || updated) : x));
      setPathDetail(d => ({ ...d, ...editForm }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeletePath() {
    if (!selectedPath || !window.confirm(`Delete path "${selectedPath.name}"?`)) return;
    try {
      await authFetch(`/api/admin/paths/${selectedPath.id}`, token, { method: 'DELETE' });
      setPaths(p => p.filter(x => x.id !== selectedPath.id));
      setSelectedPath(null);
      setPathDetail(null);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleAddModule() {
    if (!selectedPath || !selectedDeckId) return;
    try {
      const res = await authFetch(`/api/admin/paths/${selectedPath.id}/modules`, token, {
        method: 'POST',
        body: JSON.stringify({ deck_id: selectedDeckId }),
      });
      if (!res.ok) throw new Error('Failed to add module');
      await selectPath(selectedPath);
      setAddingDeck(false);
      setSelectedDeckId('');
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRemoveModule(moduleId) {
    if (!selectedPath) return;
    try {
      await authFetch(`/api/admin/paths/${selectedPath.id}/modules/${moduleId}`, token, { method: 'DELETE' });
      setPathDetail(d => ({ ...d, modules: d.modules.filter(m => m.id !== moduleId) }));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleGenerate(deckId, deckTitle) {
    setGenStatus(s => ({ ...s, [deckId]: { loading: true, message: '' } }));
    try {
      const body = {
        mode: genForm.mode,
        cardCount: Number(genForm.cardCount),
      };
      if (genForm.target.trim()) body.target = genForm.target.trim();
      else if (genForm.mode !== 'from-chat') body.target = deckTitle;

      const res = await authFetch(`/api/learning/decks/${deckId}/generate`, token, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      setGenStatus(s => ({
        ...s,
        [deckId]: { loading: false, message: `Generated ${data.cards?.length ?? 0} cards${data.skipped ? ` (${data.skipped} skipped)` : ''}` },
      }));
      // Refresh path detail to update card counts
      await selectPath(selectedPath);
      setGeneratingDeckId(null);
    } catch (err) {
      setGenStatus(s => ({ ...s, [deckId]: { loading: false, message: `Error: ${err.message}` } }));
    }
  }

  if (loading) return <div className="admin-section"><p style={{ color: 'var(--text-tertiary)' }}>Loading...</p></div>;

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <div>
          <h2 className="admin-section-title">Learning Paths</h2>
          <p className="admin-section-desc">{paths.length} path{paths.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn-sm btn-primary" onClick={() => setShowNewForm(v => !v)}>
          <Plus size={14} /> New Path
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {showNewForm && (
        <form className="admin-inline-form" onSubmit={handleCreate}>
          <div className="admin-form-row">
            <label>Name</label>
            <input value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} placeholder="Path name" required />
          </div>
          <div className="admin-form-row">
            <label>Description</label>
            <input value={newForm.description} onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description" />
          </div>
          <div className="admin-form-row-group">
            <div className="admin-form-row">
              <label>Icon</label>
              <input value={newForm.icon} onChange={e => setNewForm(f => ({ ...f, icon: e.target.value }))} placeholder="📚" style={{ width: '80px' }} />
            </div>
            <div className="admin-form-row">
              <label>Color</label>
              <input type="color" value={newForm.color} onChange={e => setNewForm(f => ({ ...f, color: e.target.value }))} style={{ width: '60px', padding: '2px', height: '36px' }} />
            </div>
            <div className="admin-form-row">
              <label>Mode</label>
              <select value={newForm.mode} onChange={e => setNewForm(f => ({ ...f, mode: e.target.value }))}>
                <option value="sequential">Sequential</option>
                <option value="free">Free</option>
              </select>
            </div>
          </div>
          <div className="admin-form-actions">
            <button type="button" className="btn-sm btn-ghost" onClick={() => setShowNewForm(false)}>Cancel</button>
            <button type="submit" className="btn-sm btn-primary" disabled={newLoading}>
              {newLoading ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      <div className="split-panel">
        <div className="split-panel-left">
          {paths.length === 0 && <div className="empty-admin"><p>No paths yet. Create one to get started.</p></div>}
          {paths.map(path => (
            <div
              key={path.id}
              className={`admin-list-item ${selectedPath?.id === path.id ? 'active' : ''}`}
              onClick={() => selectPath(path)}
            >
              <div className="admin-list-item-icon" style={{ color: path.color || 'var(--accent)' }}>
                {path.icon || '📚'}
              </div>
              <div className="admin-list-item-info">
                <div className="admin-list-item-name">{path.name}</div>
                <div className="admin-list-item-meta">
                  <span className="badge badge-info">{MODE_LABELS[path.mode] || path.mode}</span>
                  <span>{path.moduleCount ?? path.module_count ?? 0} modules</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="split-panel-right">
          {!selectedPath && (
            <div className="empty-admin"><p>Select a path to view details.</p></div>
          )}
          {selectedPath && detailLoading && (
            <p style={{ color: 'var(--text-tertiary)' }}>Loading…</p>
          )}
          {selectedPath && !detailLoading && pathDetail && editForm && (
            <div className="admin-detail">
              <div className="admin-detail-header">
                <h3>{pathDetail.name}</h3>
                <button className="btn-sm btn-danger" onClick={handleDeletePath}>
                  <Trash2 size={13} /> Delete Path
                </button>
              </div>

              <form onSubmit={handleUpdate}>
                <div className="admin-form-row">
                  <label>Name</label>
                  <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="admin-form-row">
                  <label>Description</label>
                  <input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="admin-form-row-group">
                  <div className="admin-form-row">
                    <label>Icon</label>
                    <input value={editForm.icon} onChange={e => setEditForm(f => ({ ...f, icon: e.target.value }))} style={{ width: '80px' }} />
                  </div>
                  <div className="admin-form-row">
                    <label>Color</label>
                    <input type="color" value={editForm.color} onChange={e => setEditForm(f => ({ ...f, color: e.target.value }))} style={{ width: '60px', padding: '2px', height: '36px' }} />
                  </div>
                  <div className="admin-form-row">
                    <label>Mode</label>
                    <select value={editForm.mode} onChange={e => setEditForm(f => ({ ...f, mode: e.target.value }))}>
                      <option value="sequential">Sequential</option>
                      <option value="free">Free</option>
                    </select>
                  </div>
                </div>
                <button type="submit" className="btn-sm btn-primary" disabled={saving} style={{ marginTop: '8px' }}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </form>

              <div className="admin-detail-section">
                <div className="admin-detail-section-header">
                  <span>Modules ({pathDetail.modules?.length || 0})</span>
                  <button className="btn-sm btn-ghost" onClick={() => setAddingDeck(v => !v)}>
                    <Plus size={13} /> Add Module
                  </button>
                </div>

                {addingDeck && (
                  <div className="admin-inline-form" style={{ marginBottom: '12px' }}>
                    <div className="admin-form-row">
                      <label>Select Deck</label>
                      <select value={selectedDeckId} onChange={e => setSelectedDeckId(e.target.value)}>
                        <option value="">— choose deck —</option>
                        {decks.map(d => (
                          <option key={d.id} value={d.id}>{d.title}</option>
                        ))}
                      </select>
                    </div>
                    <div className="admin-form-actions">
                      <button className="btn-sm btn-ghost" onClick={() => setAddingDeck(false)}>Cancel</button>
                      <button className="btn-sm btn-primary" onClick={handleAddModule} disabled={!selectedDeckId}>Add</button>
                    </div>
                  </div>
                )}

                {(pathDetail.modules || []).length === 0 && (
                  <p style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No modules yet.</p>
                )}
                {(pathDetail.modules || []).map((mod, i) => {
                  const deckId = mod.deck_id;
                  const isGenerating = generatingDeckId === deckId;
                  const status = genStatus[deckId];
                  return (
                    <div key={mod.id} style={{ marginBottom: '6px' }}>
                      <div className="module-row">
                        <GripVertical size={14} style={{ color: 'var(--text-tertiary)' }} />
                        <span className="module-index">{i + 1}</span>
                        <div className="module-info">
                          <span className="module-name">{mod.title || mod.deck_title}</span>
                          <span className="module-meta">{mod.cardCount ?? mod.card_count ?? 0} cards</span>
                        </div>
                        <button
                          className="action-btn"
                          title="Generate cards with AI"
                          onClick={() => setGeneratingDeckId(isGenerating ? null : deckId)}
                          style={{ color: 'var(--accent)' }}
                        >
                          <Sparkles size={13} />
                        </button>
                        <button className="action-btn action-btn-danger" onClick={() => handleRemoveModule(mod.id)}>
                          <X size={13} />
                        </button>
                      </div>

                      {isGenerating && (
                        <div className="admin-inline-form" style={{ marginTop: '6px', marginLeft: '36px' }}>
                          <div className="admin-form-row-group">
                            <div className="admin-form-row">
                              <label>Cards</label>
                              <input
                                type="number"
                                min="5" max="200"
                                value={genForm.cardCount}
                                onChange={e => setGenForm(f => ({ ...f, cardCount: e.target.value }))}
                                style={{ width: '72px' }}
                              />
                            </div>
                            <div className="admin-form-row">
                              <label>Source</label>
                              <select value={genForm.mode} onChange={e => setGenForm(f => ({ ...f, mode: e.target.value }))}>
                                <option value="concept">Concept / topic</option>
                                <option value="module">Module context</option>
                                <option value="from-chat">Paste text</option>
                              </select>
                            </div>
                          </div>
                          {genForm.mode === 'from-chat' ? (
                            <div className="admin-form-row">
                              <label>Content</label>
                              <textarea
                                rows={4}
                                placeholder="Paste document text or topic notes here…"
                                value={genForm.target}
                                onChange={e => setGenForm(f => ({ ...f, target: e.target.value }))}
                                style={{ width: '100%', resize: 'vertical' }}
                              />
                            </div>
                          ) : (
                            <div className="admin-form-row">
                              <label>Topic</label>
                              <input
                                placeholder={`e.g. "${mod.title || mod.deck_title}" (leave blank to use deck title)`}
                                value={genForm.target}
                                onChange={e => setGenForm(f => ({ ...f, target: e.target.value }))}
                              />
                            </div>
                          )}
                          {status?.message && (
                            <p style={{ fontSize: '12px', color: status.message.startsWith('Error') ? 'var(--error)' : 'var(--success, #00ff88)', margin: '4px 0' }}>
                              {status.message}
                            </p>
                          )}
                          <div className="admin-form-actions">
                            <button className="btn-sm btn-ghost" onClick={() => setGeneratingDeckId(null)}>Cancel</button>
                            <button
                              className="btn-sm btn-primary"
                              disabled={status?.loading}
                              onClick={() => handleGenerate(deckId, mod.title || mod.deck_title)}
                            >
                              {status?.loading ? 'Generating…' : `Generate ${genForm.cardCount} cards`}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
